import Head from 'next/head';
import React, { useState, ReactElement } from 'react';
import { Layout } from '../components/shared/layout/layout';
import {
  CheckCircle,
  Users,
  Calendar,
  Database,
  LayoutDashboard,
  Download,
} from 'lucide-react';
import { FaSalesforce, FaHubspot } from 'react-icons/fa';
import OnboardingCard from '../components/shared/OnboardingCard';
import { CrmConnectionNameModal } from '../components/shared/CrmConnectionNameModal';
import { useUser } from '@auth0/nextjs-auth0';
import useSWR from 'swr';
import SchedulerEnhancementModal from '../components/events/SchedulerEnhacement';
import isTeamAdminUser from '../utils/helpers/getUserRole';
import { isPayingUser } from '../utils/helpers/userHelper';
import { useRouter } from 'next/router';
import TeamAdminBanner from '../components/team/TeamAdminBanner';
import {
  FlexCalList,
  FlexCalSearchData,
  Place,
  VirtualLocation,
  NextPageWithLayout,
  CrmConnectionInfo,
  CrmAuthRequest,
  CrmAuthResponse,
} from '../utils/types';
import { UserInfo } from '../utils/interfaces';
import { ClockLoader } from 'react-spinners';
import { Edition, CrmType, CrmLabels, CrmAuthSlugs } from '../utils/enums';

const fetcher = (url: string) => fetch(url).then((res) => res.json());

const Home: NextPageWithLayout<unknown> = () => {
  const { user, error, isLoading } = useUser();
  const userInfo = user as UserInfo;
  const edition = userInfo?.accountDetails?.edition;
  const isTeamAccount = edition === Edition.Team;
  const router = useRouter();
  const isTeamAdmin = isTeamAdminUser(userInfo);
  const isPremium = isPayingUser(userInfo);
  const [isSchedulerModalOpen, setIsSchedulerModalOpen] = useState(false);
  const [crmTypeToConnect, setCrmTypeToConnect] = useState<CrmType | null>(
    null
  );
  const [flexCalParams, setFlexCalParams] = useState<
    FlexCalSearchData | undefined
  >({
    pageNumber: 0,
    resultsPerPage: 10,
    nameFilter: '',
  });

  // Check if user is a new user (doesn't exist in backend)
  const { data: subscriberExistsData, error: subscriberExistsError } = useSWR(
    user ? '/api/account/subscriberExists' : null,
    fetcher
  );

  React.useEffect(() => {
    if (
      user &&
      subscriberExistsData &&
      subscriberExistsData.subscriberExists === false
    ) {
      router.push('/signup/completeSignup');
    }
  }, [user, subscriberExistsData, router]);

  // Data fetching for the modal
  const { data: physicalLocations } = useSWR<Place[]>(
    '/api/locations/physical'
  );
  const { data: virtualLocations } = useSWR<VirtualLocation[]>(
    '/api/locations/virtual'
  );
  const { data: connectedCrmsData, mutate: mutateConnectedCrms } = useSWR<
    CrmConnectionInfo[]
  >(user?.sub ? '/api/crm/connections' : null);
  const connectedCrms = connectedCrmsData ?? [];
  const { data: flexCalListData, isValidating: isFlexCalLoading } = useSWR(
    `/api/flexCals/search?pageNumber=${flexCalParams?.pageNumber}&resultsPerPage=${flexCalParams?.resultsPerPage}&nameFilter=${flexCalParams?.nameFilter}`,
    fetcher
  );

  const flexCalList: FlexCalList | undefined = flexCalListData
    ? {
        flexCalSummaries: flexCalListData.lazyCalendars ?? [],
        totalRecords: flexCalListData.totalRecords,
      }
    : undefined;

  const handleSaveScheduler = async (data: any) => {
    console.log('Onboarding Scheduler Data:', data);
    setIsSchedulerModalOpen(false);
    if (data?.lazyCalendarId) {
      router.push(`/scheduler?flexcalid=${data.lazyCalendarId}`);
    } else {
      router.push('/scheduler');
    }
  };

  const handleCrmLogin = async (crmType: CrmType, name: string) => {
    const label = CrmLabels[crmType];
    try {
      const apiSlug = CrmAuthSlugs[crmType];
      const currentUrl = window.location.origin + '/';

      const request: CrmAuthRequest = { returnUrl: currentUrl, name };
      const response = await fetch(`/api/crm/auth/${apiSlug}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });
      if (response.ok) {
        const data: CrmAuthResponse = await response.json();
        if (data.loginUrl) {
          window.location.href = data.loginUrl;
        } else {
          await mutateConnectedCrms();
        }
      }
    } catch (error) {
      console.error(`Error initiating ${label} login:`, error);
    }
  };

  const isCheckingSubscriber =
    !!user && subscriberExistsData === undefined && !subscriberExistsError;
  const isRedirectingToSignup =
    !!user && subscriberExistsData?.subscriberExists === false;

  if (isLoading || isCheckingSubscriber || isRedirectingToSignup) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white h-screen">
        <ClockLoader color="#2CA6A4" />
      </div>
    );
  }
  if (error) return <div>{error.message}</div>;

  // Unauthenticated view
  if (!user) {
    return (
      <div className="min-h-screen bg-white">
        <Head>
          <title>Confirmed</title>
          <meta name="description" content="Welcome to Confirmed" />
          <link rel="icon" href="/favicon.ico" />
        </Head>

        <main className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] px-4">
          <div className="w-full max-w-4xl flex flex-col gap-12 items-center">
            <div className="w-full flex justify-center">
              <img
                src="/reservation_confirmation.png"
                alt="Confirmed Illustration"
                className="w-full max-w-2xl h-auto object-contain"
              />
            </div>
            <img
              src="/confirmedTMLogo.svg"
              alt="Confirmed Logo"
              className="h-10 w-auto"
            />
          </div>
        </main>
      </div>
    );
  }

  // Authenticated view
  return (
    <div className="relative min-h-screen">
      <Head>
        <title>Confirmed Dashboard</title>
        <meta name="description" content="Manage your Confirmed connections" />
        <link rel="icon" href="/favicon.ico" />
      </Head>

      {/* Background Image Container */}
      <div className="fixed inset-0 z-0 flex items-center justify-center opacity-10 pointer-events-none">
        <img
          src="/reservation_confirmation.png"
          alt="Background"
          className="w-2/3 object-contain"
        />
      </div>

      <main className="relative z-10 py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-50/80 backdrop-blur-sm rounded-full mb-4 shadow-sm">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="text-4xl font-black text-gray-900 mb-4 tracking-tight">
              You're Ready to <span className="text-[#2CA6A4]">Connect!</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto font-medium">
              Welcome to Confirmed. You now have a{' '}
              {isTeamAccount ? 'Team ' : ''}
              Superconnector account. Choose how you want to start your journey
              today.
            </p>
          </div>

          {/* Team Admin Banner */}
          {isTeamAdmin && <TeamAdminBanner />}

          {/* Grid Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Card 1: Encounters */}
            <OnboardingCard
              title="Encounters"
              description="Turn every in-person conversation into a saved connection with context, follow-up, and CRM sync in seconds."
              icon={Users}
              buttonText="Start now"
              buttonHref="/encounters"
            />

            {/* Card 2: Scheduler */}
            <OnboardingCard
              title="Scheduler"
              description="Set up your first scheduling link using our easy wizard and start booking meetings effortlessly."
              icon={Calendar}
              buttonText="Create now"
              onClick={() => setIsSchedulerModalOpen(true)}
            />

            {/* Card 3: CRM Suite */}
            <OnboardingCard
              title="CRM Suite"
              description="Boost your productivity by connecting Confirmed to your existing workflow and CRM tools."
              icon={Database}
              secondaryActions={[
                {
                  label: 'Outlook Add-in',
                  href: 'https://marketplace.microsoft.com/en-us/product/WA200005922',
                  icon: Download,
                  isExternal: true,
                },
                {
                  label: 'Connect Salesforce',
                  onClick: () => setCrmTypeToConnect(CrmType.Salesforce),
                  icon: FaSalesforce,
                },
                {
                  label: 'Connect HubSpot',
                  onClick: () => setCrmTypeToConnect(CrmType.HubSpot),
                  icon: FaHubspot,
                },
              ]}
            />

            {/* Card 4: Dashboard */}
            <OnboardingCard
              title="Event-Host Dashboard"
              description="Manage your events, attendees, and networking all in one central location."
              icon={LayoutDashboard}
              buttonText="Start Now"
              buttonHref="/event/hostdashboard"
            />
          </div>

          {/* Footer Note */}
          <div className="mt-12 text-center text-gray-500 text-sm">
            <p>
              Need help getting started?{' '}
              <a
                href="mailto:support@confirmedapp.com"
                className="text-[#2CA6A4] font-semibold hover:underline"
              >
                Contact our support team
              </a>
            </p>
          </div>
        </div>
      </main>

      {/* Scheduler Modal */}
      {isSchedulerModalOpen && (
        <SchedulerEnhancementModal
          isOpen={true}
          eventName=""
          isSignupFlow={true}
          onSkip={() => setIsSchedulerModalOpen(false)}
          onSave={handleSaveScheduler}
          flexCalList={flexCalList}
          flexCalParams={flexCalParams}
          setFlexCalParams={setFlexCalParams}
          isFlexCalLoading={isFlexCalLoading}
          isPremium={isPremium}
          physicalLocations={physicalLocations}
          virtualLocations={virtualLocations}
          userInfo={userInfo}
        />
      )}

      <CrmConnectionNameModal
        crmType={crmTypeToConnect}
        connectedCrms={connectedCrms}
        onCancel={() => setCrmTypeToConnect(null)}
        onConfirm={(name) => {
          if (crmTypeToConnect === null) return;
          const crmType = crmTypeToConnect;
          setCrmTypeToConnect(null);
          handleCrmLogin(crmType, name);
        }}
      />

      {/* Footer Logo */}
      <div className="flex items-center justify-center mb-16 relative z-10">
        <img
          className="w-auto h-8"
          src="/confirmedTMLogo.svg"
          alt="Confirmed Logo"
        />
      </div>
    </div>
  );
};

Home.getLayout = function (page: ReactElement) {
  return <Layout>{page}</Layout>;
};

export default Home;
